/**
 * MessageBubble — user and assistant chat bubbles (Plan 05-07b).
 * Extended in Plan 05-08a: integrates AllergyFilterBanner, ChildAdvisoryFooter,
 * and ToolCallTrace as conditional decorations.
 *
 * #158 (steg 8a, Stell_Assistent):
 *   - Brukerboblen er fylt `bg-solid text-on-solid` til høyre, 15/600, med
 *     det spisse hjørnet nede til høyre.
 *   - Assistentboblen er glass (`bg-ai-bubble-bg` = kortflaten, kantlinje og
 *     blur) til venstre, med det spisse hjørnet nede til venstre.
 *   - Under assistentboblen står, i denne rekkefølgen, «Sjekk med en voksen»
 *     (ChildAdvisoryFooter) som en amber linje og brikken «Hentet data».
 *   - AllergyFilterBanner står fortsatt OVER den korrigerte boblen.
 *
 * Supports:
 * - Markdown rendering via renderMarkdown (XSS-safe by construction)
 * - StreamingCursor appended when isStreaming=true
 * - filterCorrected: renders AllergyFilterBanner ABOVE the bubble (non-dismissable)
 * - advisoryLine: ChildAdvisoryFooter som amber linje under boblen (#158)
 * - toolCalls: renders ToolCallTrace below bubble markdown
 * - memoryChanges: MemoryChips under boblen — «Husket: …» med Angre,
 *   «Foreslått for foreldrene: …», «Glemt: …» (AI-21, #193)
 * - Fade-up entry animation via motion/react (instant under reduced motion)
 *
 * data-testid: assistant-bubble-{role}-{index}
 * role="article" per UI-SPEC §Accessibility
 *
 * Memoised, and deliberately so. Without it, every streamed token re-rendered
 * the whole thread and re-ran renderMarkdown for *every* message in it, so the
 * cost of streaming one reply was O(tokens x total thread length) — quadratic,
 * on a Pi-served kitchen tablet. With memo, a completed bubble does not
 * re-render at all while a new reply streams; only the streaming bubble
 * re-parses, and only its own text.
 *
 * This relies on message objects being referentially stable. AssistantRoute
 * appends with setMessages(prev => [...prev, msg]), so existing entries keep
 * identity. If that ever changes to rebuilding entries per render, this memo
 * silently stops helping — the render cost just quietly returns.
 */
import { memo, useMemo } from 'react';
import { motion } from 'motion/react';
import { renderMarkdown } from './markdown';
import { StreamingCursor } from './StreamingCursor';
import { AllergyFilterBanner } from './AllergyFilterBanner';
import { ChildAdvisoryFooter } from './ChildAdvisoryFooter';
import { ToolCallTrace } from './ToolCallTrace';
import type { ToolCallSummary } from './ToolCallTrace';
import { MemoryChips } from './MemoryChip';
import type { MemoryChipEntry } from './useAssistantStream';

export interface MessageBubbleProps {
  role: 'user' | 'assistant';
  index: number;
  content: string;
  isStreaming?: boolean;
  // Decoration slots — wired in plan 05-08a:
  filterCorrected?: boolean | undefined;
  advisoryLine?: boolean | undefined;
  toolCalls?: ToolCallSummary[] | undefined;
  /** AI-21 (#193): `memory_changed`-hendelsene i turen (#212: eller hydrert fra historikken). */
  memoryChanges?: readonly MemoryChipEntry[] | undefined;
}

export const MessageBubble = memo(function MessageBubble(props: MessageBubbleProps) {
  const isUser = props.role === 'user';

  // Keyed on content alone: re-parsing is pure. For a completed bubble memo
  // already prevents the re-render; this additionally avoids re-parsing when
  // only a decoration prop changes (e.g. tool calls landing on the streaming
  // bubble while its text is unchanged).
  const body = useMemo(() => renderMarkdown(props.content), [props.content]);

  return (
    <>
      {/* AllergyFilterBanner appears ABOVE the corrected assistant bubble */}
      {props.role === 'assistant' && props.filterCorrected && <AllergyFilterBanner />}

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={isUser ? 'my-2 flex justify-end' : 'my-2 flex flex-col items-start gap-2'}
        data-testid={`assistant-bubble-${props.role}-${props.index}`}
        role="article"
        aria-busy={props.isStreaming ? true : undefined}
      >
        {/* En tur uten tekst kan ha brikker (#193); da tegnes ingen tom boble. */}
        {props.content !== '' || props.isStreaming ? (
          <div
            className={
              isUser
                // Lenker i markdown har sin egen farge; på den fylte boblen
                // arver de on-solid, så de ikke blir mørkt på mørkt.
                ? 'ml-auto max-w-39/50 rounded-row rounded-br-xs bg-solid px-4 py-3 text-base font-semibold leading-snug text-on-solid [&_a]:text-on-solid'
                // text-ai-bubble-fg, ikke text-ink: boblen har sitt eget
                // bakgrunnstoken og trenger sitt eget forgrunnstoken (#29).
                // Begge peker på rolletokenene (card/ink).
                : 'mr-auto max-w-22/25 rounded-row rounded-bl-xs border border-card-line bg-ai-bubble-bg px-4 py-3 text-base leading-normal text-ai-bubble-fg shadow-md backdrop-blur-card'
            }
          >
            {body}
            {props.isStreaming && <StreamingCursor />}
          </div>
        ) : null}
        {/* «Sjekk med en voksen» som amber linje under boblen (#158) */}
        {props.advisoryLine && <ChildAdvisoryFooter />}
        {/* ToolCallTrace below the bubble content (assistant only) */}
        {props.role === 'assistant' && (props.toolCalls?.length ?? 0) > 0 && (
          <ToolCallTrace toolCalls={props.toolCalls!} />
        )}
        {props.role === 'assistant' && (props.memoryChanges?.length ?? 0) > 0 && (
          <MemoryChips changes={props.memoryChanges!} />
        )}
      </motion.div>
    </>
  );
});
